import React, { FC, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Select, { SingleValue } from "react-select";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import useAxios from "../../../hooks/useAxios";
import { API_ENDPOINTS } from "../../../constants/api";
import { toaster } from "../../../utils/toast";
import { RootState } from "../../../redux/rootReducer";
import { ROLE } from "../../../utils/enums";
import DnsDetailCard from "./dnsDetail";

interface LocationOption {
  value: string;
  label: string;
}

interface VerifyDomainProps {
  show: boolean;
  handleClose: () => void;
  dnsDetail: any;
}

const VerifyDomain: FC<VerifyDomainProps> = ({ show, handleClose, dnsDetail }) => {
  const { postData, isLoading } = useAxios();
  const [dnsDetailData, setDnsDetailData] = useState(dnsDetail);
  const { orgLocations, selectedLocationId, role_id } = useSelector((state: RootState) => state.user);

  const isOrgAdmin = [ROLE.ORG, ROLE.ACCOUNT_ADMIN].includes(+role_id);
  const isLocationAdmin = +role_id === ROLE.LOCATION_ADMIN;

  const locationOptions: LocationOption[] = (orgLocations ?? []).map((l: any) => ({
    value: String(l.id || l.location_id),
    label: l.display_name || l.address || `Location #${l.id || l.location_id}`,
  }));

  const allLocationOptions: LocationOption[] = [
    ...(isOrgAdmin ? [{ value: "", label: "Organisation-wide (All Locations)" }] : []),
    ...locationOptions,
  ];

  const defaultLocationId = isOrgAdmin
    ? ""
    : (selectedLocationId ? String(selectedLocationId) : (locationOptions[0]?.value ?? ""));

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email address")
      .matches(
        /^[a-zA-Z][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        'Invalid email address'
      )
      .required("Email is required"),
    location_id: isLocationAdmin
      ? Yup.string().required("Please select a location")
      : Yup.string().nullable(),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      location_id: defaultLocationId,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const { email, location_id } = values;
      let payload = {
        email: email.trim().toLowerCase(),
        location_id: location_id ? Number(location_id) : null,
      };
      submitHandler({ payload });
    },
  });

  const selectedLocationOption = allLocationOptions.find(
    (opt) => opt.value === String(formik.values.location_id ?? "")
  ) ?? null;

  const isLocationInvalid = Boolean(
    (formik.touched.location_id || formik.submitCount > 0) &&
    formik.errors.location_id
  );

  const submitHandler = async ({ payload }: any) => {
    try {
      const response = await postData(API_ENDPOINTS.VERIFY_DOMAIN, payload);
      if (response) {
        const { message, success, data } = response;
        toaster({ message, success });
        !data || data.length === 0 ? handleClose() : setDnsDetailData(data);
      } else {
        handleClose();
        let message = "Error saving domain";
        let success = false;
        toaster({ message, success });
      }
    } catch (error) {}
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="domains-modal">
      <Modal.Header closeButton>
        <Modal.Title>Add Domain</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!dnsDetailData && (
          <>
            <p>
              Verify Your Domain Ownership: Add TXT Records to Your Domain Registrar Console (e.g., GoDaddy, Porkbun) by Entering an Email Address ( *@yourdomain.com )
            </p>
            <Form onSubmit={formik.handleSubmit}>
              <Form.Group className="form-group mb-3" controlId="formLocation">
                <Form.Label>Assign Location</Form.Label>
                <Select
                  classNamePrefix="location-select"
                  options={allLocationOptions}
                  value={selectedLocationOption}
                  onChange={(option: SingleValue<LocationOption>) => {
                    formik.setFieldValue("location_id", option ? option.value : "");
                    formik.setFieldTouched("location_id", true);
                  }}
                  onBlur={() => {
                    formik.setFieldTouched("location_id", true);
                  }}
                  isSearchable
                  placeholder="Select location"
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: "58px",
                      borderRadius: "6px",
                      borderColor: isLocationInvalid ? "#dc3545" : state.isFocused ? "#86b7fe" : "#E6E6E6",
                      boxShadow: isLocationInvalid
                        ? "0 0 0 0.25rem rgba(220, 53, 69, 0.25)"
                        : state.isFocused
                        ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
                        : "none",
                      "&:hover": {
                        borderColor: isLocationInvalid ? "#dc3545" : "#b3b3b3",
                      },
                      fontSize: "16px",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: "0 16px",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "#878787",
                      fontSize: "16px",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "#000",
                      fontSize: "16px",
                    }),
                    indicatorSeparator: () => ({
                      display: "none",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                />
                {formik.touched.location_id && formik.errors.location_id && (
                  <div className="text-danger">{formik.errors.location_id as string}</div>
                )}
                <Form.Text className="text-muted d-block mt-1">
                  {formik.values.location_id
                    ? "This domain will be dedicated to the selected location."
                    : "This domain will be available across all current and future locations in the organisation."}
                </Form.Text>
              </Form.Group>

              <Form.Group className="form-group" controlId="formBasicEmail">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="e.g. info@yourdomain.com"
                  {...formik.getFieldProps("email")}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="text-danger">{formik.errors.email}</div>
                )}
              </Form.Group>
            </Form>
          </>
        )}

        {dnsDetailData && <DnsDetailCard DnsDetailData={dnsDetailData} />}
      </Modal.Body>
      <Modal.Footer className="justify-content-center">
        <Button
          variant="primary"
          type="button"
          className="btn-theme"
          onClick={() => {
            !dnsDetailData ? formik.handleSubmit() : handleClose();
          }}
          disabled={isLoading}
        >
          {isLoading ? "Loading…" : !dnsDetailData ? "Add Domain" : "Close"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VerifyDomain;
